import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, Mail, Star, CalendarDays, Award, Users, Grid3x3, AlertTriangle, MapPin, Video, Download, Send, FileText, Trash2, Upload, Loader2, BadgeCheck, ShieldCheck, Clock } from 'lucide-react';
import api from '../api/client';
import ConfirmDialog from '../components/ConfirmDialog';
import DetailSheet from '../components/DetailSheet';
import RowActions from '../components/RowActions';
import EmptyState from '../components/EmptyState';
import { SkeletonCard } from '../components/Skeleton';
import { toast } from '../hooks/use-toast';
import { sortNewestFirst } from '../utils/sort';
import TrainerExtranetDialog from '../components/TrainerExtranetDialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';

const DOMAINS = [
  'Informatique & Digital',
  'Management & Leadership',
  'Ressources humaines',
  'Langues',
  'Sécurité & Prévention',
  'Commerce & Vente',
  'Finance & Comptabilité',
  'Marketing & Communication',
  'Qualité & Conformité',
  'Santé & Social',
  'Industrie & Technique',
  'Bureautique',
  'Développement personnel',
];

const DOC_TYPES = [
  { value: 'CV', label: 'CV' },
  { value: 'DIPLOME', label: 'Diplôme' },
  { value: 'RIB', label: 'RIB' },
  { value: 'KBIS', label: 'Kbis / URSSAF' },
  { value: 'AUTRE', label: 'Autre' },
];

/** Human-readable relative time for the extranet last access badge. */
function formatRelativeConnection(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Connecté à l'instant";
  if (mins < 60) return `Connecté il y a ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Connecté il y a ${hrs} h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `Connecté il y a ${days} j`;
  const months = Math.floor(days / 30);
  if (months < 12) return `Connecté il y a ${months} mois`;
  const years = Math.floor(months / 12);
  return `Connecté il y a ${years} an${years > 1 ? 's' : ''}`;
}

/** Trainer form with SIRET autofill + RH-light enrichments (Vague 99.6).
 *  Tabbed layout: Identité · Pro/Légal · Tarifs · Compétences · Documents.
 */
function TrainerFormDialog({ open, onOpenChange, initialValues, onSubmit }) {
  const empty = {
    name: '', email: '', phone: '', status: 'Freelance',
    speciality: '', domain: '', domain_other: '',
    siret: '', siren: '', nda: '', legal_form: '',
    address: '', postal_code: '', city: '',
    sessions: 0, rating: 0,
    // Vague 99.6 — RH-light freelance fields
    rc_pro_insurer: '', rc_pro_policy: '', rc_pro_expires_at: '',
    certifications: [],
    daily_rate_eur: '', hourly_rate_eur: '', payment_terms_days: 30,
    languages: [], mobility_km: '',
    availability_status: 'available', availability_note: '',
    linkedin_url: '',
  };
  const [values, setValues] = useState(empty);
  const [tab, setTab] = useState('identite');
  const [loading, setLoading] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [error, setError] = useState(null);
  const [certInput, setCertInput] = useState('');
  const [langInput, setLangInput] = useState('');

  useEffect(() => {
    if (!open) return;
    if (initialValues) {
      const known = DOMAINS.includes(initialValues.domain);
      const rcp = initialValues.rc_pro || {};
      setValues({
        ...empty, ...initialValues,
        domain: known ? initialValues.domain : (initialValues.domain ? '__OTHER__' : ''),
        domain_other: known ? '' : (initialValues.domain || ''),
        rc_pro_insurer: rcp.insurer || initialValues.rc_pro_insurer || '',
        rc_pro_policy: rcp.policy || initialValues.rc_pro_policy || '',
        rc_pro_expires_at: rcp.expires_at || initialValues.rc_pro_expires_at || '',
        certifications: initialValues.certifications || [],
        languages: initialValues.languages || [],
        availability_status: initialValues.availability_status || 'available',
      });
    } else {
      setValues(empty);
    }
    setTab('identite');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialValues]);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const sireneLookup = async () => {
    const q = (values.siret || values.siren || '').replace(/\s/g, '');
    if (!q || q.length < 9) { toast({ title: 'Saisissez un SIRET (14) ou SIREN (9)' }); return; }
    setLookingUp(true);
    try {
      const { data } = await api.get('/companies/sirene-lookup', { params: { q } });
      setValues((s) => ({
        ...s,
        siret: data.siret || s.siret,
        siren: data.siren || s.siren,
        legal_form: data.legal_form || s.legal_form,
        address: data.address_line || s.address,
        postal_code: data.postal_code || s.postal_code,
        city: data.city || s.city,
        name: s.name || data.name || '',
      }));
      toast({ title: 'Données SIRENE récupérées', description: data.name });
    } catch (e) {
      toast({ title: 'Échec SIRENE', description: e?.response?.data?.detail || 'Erreur réseau' });
    }
    setLookingUp(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const finalDomain = values.domain === '__OTHER__' ? (values.domain_other || '').trim() : values.domain;
      const payload = {
        ...values,
        domain: finalDomain,
        sessions: Number(values.sessions) || 0,
        rating: Number(values.rating) || 0,
        daily_rate_eur: values.daily_rate_eur === '' ? null : Number(values.daily_rate_eur) || 0,
        hourly_rate_eur: values.hourly_rate_eur === '' ? null : Number(values.hourly_rate_eur) || 0,
        payment_terms_days: Number(values.payment_terms_days) || 30,
        mobility_km: values.mobility_km === '' ? null : Number(values.mobility_km) || 0,
        rc_pro: (values.rc_pro_insurer || values.rc_pro_policy || values.rc_pro_expires_at) ? {
          insurer: values.rc_pro_insurer || '',
          policy: values.rc_pro_policy || '',
          expires_at: values.rc_pro_expires_at || null,
        } : null,
      };
      delete payload.domain_other;
      delete payload.rc_pro_insurer;
      delete payload.rc_pro_policy;
      delete payload.rc_pro_expires_at;
      await onSubmit(payload);
      onOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Erreur inattendue');
    }
    setLoading(false);
  };

  const addChip = (key, current, setter) => {
    const v = (current || '').trim();
    if (!v) return;
    if ((values[key] || []).includes(v)) { setter(''); return; }
    set(key, [...(values[key] || []), v]);
    setter('');
  };
  const removeChip = (key, idx) => set(key, (values[key] || []).filter((_, i) => i !== idx));

  const TABS = [
    { id: 'identite',    label: 'Identité' },
    { id: 'pro',         label: 'Pro / Légal' },
    { id: 'tarifs',      label: 'Tarifs & dispo' },
    { id: 'qualifs',     label: 'Qualifications' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[680px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[18px]" style={{ fontFamily: "'Satoshi','Plus Jakarta Sans',sans-serif", letterSpacing: '-0.02em' }}>
            {initialValues ? 'Modifier le formateur' : 'Nouveau formateur'}
          </DialogTitle>
          <DialogDescription className="text-[13px]">
            Profil complet : identité, légal, tarifs, qualifications.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-1 border-b border-[#EFF0F6] -mt-1 mb-3 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)}
                    data-testid={`trainer-tab-${t.id}`}
                    className={`px-3 py-2 text-[12.5px] font-semibold border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-[#6B6C85] hover:text-[#1A1B2E]'}`}>
              {t.label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          {tab === 'identite' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Nom complet" required value={values.name} onChange={(v) => set('name', v)} testId="trainer-field-name" full />
                <Field label="Email" type="email" required value={values.email} onChange={(v) => set('email', v)} testId="trainer-field-email" />
                <Field label="Téléphone" value={values.phone} onChange={(v) => set('phone', v)} testId="trainer-field-phone" />
                <Field label="Statut" as="select" required value={values.status} onChange={(v) => set('status', v)}
                       options={['Interne', 'Freelance']} testId="trainer-field-status" />
                <Field label="Spécialité" required value={values.speciality} onChange={(v) => set('speciality', v)} testId="trainer-field-speciality" />
                <Field label="Domaine d'activité" as="select" value={values.domain}
                       onChange={(v) => set('domain', v)} testId="trainer-field-domain"
                       options={[...DOMAINS, { value: '__OTHER__', label: 'Autre…' }]} />
                {values.domain === '__OTHER__' && (
                  <Field label="Précisez le domaine" value={values.domain_other}
                         onChange={(v) => set('domain_other', v)} testId="trainer-field-domain-other" />
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Adresse" full value={values.address} onChange={(v) => set('address', v)} testId="trainer-field-address" />
                <Field label="Code postal" value={values.postal_code} onChange={(v) => set('postal_code', v)} testId="trainer-field-postal" />
                <Field label="Ville" value={values.city} onChange={(v) => set('city', v)} testId="trainer-field-city" />
                <Field label="Profil LinkedIn (URL)" value={values.linkedin_url} onChange={(v) => set('linkedin_url', v)} testId="trainer-field-linkedin" placeholder="https://linkedin.com/in/…" full />
              </div>
            </>
          )}

          {tab === 'pro' && (
            <>
              <div className="rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[12.5px] font-semibold text-[#4A4B63]">Identité juridique</span>
                  <button type="button" onClick={sireneLookup} disabled={lookingUp}
                          data-testid="trainer-sirene-lookup"
                          className="h-8 px-3 rounded-lg bg-[#EEF2FF] text-[#4F46E5] text-[12px] font-semibold flex items-center gap-1.5 hover:bg-[#E0E7FF] disabled:opacity-60">
                    {lookingUp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BadgeCheck className="w-3.5 h-3.5" />}
                    Récupérer depuis SIRENE
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="SIRET (14 chiffres)" value={values.siret} onChange={(v) => set('siret', v)} testId="trainer-field-siret" />
                  <Field label="SIREN (9 chiffres)" value={values.siren} onChange={(v) => set('siren', v)} testId="trainer-field-siren" />
                  <Field label="Forme juridique" value={values.legal_form} onChange={(v) => set('legal_form', v)} testId="trainer-field-legal-form" />
                  <Field label="Numéro d'activité (NDA)" value={values.nda} onChange={(v) => set('nda', v)} testId="trainer-field-nda" placeholder="Ex: 11 75 12345 75" />
                </div>
              </div>
              <div className="rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] p-3">
                <div className="text-[12.5px] font-semibold text-[#4A4B63] mb-2">Responsabilité Civile Professionnelle</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label="Assureur" value={values.rc_pro_insurer} onChange={(v) => set('rc_pro_insurer', v)} testId="trainer-field-rc-insurer" />
                  <Field label="N° de police" value={values.rc_pro_policy} onChange={(v) => set('rc_pro_policy', v)} testId="trainer-field-rc-policy" />
                  <Field label="Échéance" type="date" value={values.rc_pro_expires_at} onChange={(v) => set('rc_pro_expires_at', v)} testId="trainer-field-rc-expires" />
                </div>
              </div>
            </>
          )}

          {tab === 'tarifs' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Tarif jour (€)" type="number" value={values.daily_rate_eur} onChange={(v) => set('daily_rate_eur', v)} testId="trainer-field-daily-rate" />
                <Field label="Tarif heure (€)" type="number" value={values.hourly_rate_eur} onChange={(v) => set('hourly_rate_eur', v)} testId="trainer-field-hourly-rate" />
                <Field label="Délai de paiement (jours)" type="number" value={values.payment_terms_days} onChange={(v) => set('payment_terms_days', v)} testId="trainer-field-payment-terms" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Disponibilité" as="select" value={values.availability_status} onChange={(v) => set('availability_status', v)} testId="trainer-field-availability"
                       options={[
                         { value: 'available', label: '🟢 Disponible' },
                         { value: 'busy', label: '🟡 Occupé' },
                         { value: 'unavailable', label: '🔴 Indisponible' },
                       ]} />
                <Field label="Mobilité (km autour de l'adresse)" type="number" value={values.mobility_km} onChange={(v) => set('mobility_km', v)} testId="trainer-field-mobility" />
                <Field label="Note de disponibilité" full value={values.availability_note} onChange={(v) => set('availability_note', v)} testId="trainer-field-availability-note" placeholder="Ex: Indisponible juin 2026" />
              </div>
            </>
          )}

          {tab === 'qualifs' && (
            <>
              <div className="rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] p-3">
                <div className="text-[12.5px] font-semibold text-[#4A4B63] mb-2">Certifications</div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(values.certifications || []).map((c, i) => (
                    <span key={`${c}-${i}`} data-testid={`cert-chip-${i}`} className="px-2 h-7 rounded-full bg-[#EEF2FF] text-[#4F46E5] text-[12px] font-semibold flex items-center gap-1.5">
                      {c}
                      <button type="button" onClick={() => removeChip('certifications', i)} className="hover:text-[#B91C1C]">×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={certInput} onChange={(e) => setCertInput(e.target.value)} placeholder="Ex: Qualiopi VAE niveau 2"
                         onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addChip('certifications', certInput, setCertInput); } }}
                         data-testid="cert-input"
                         className="flex-1 h-9 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px]" />
                  <button type="button" onClick={() => addChip('certifications', certInput, setCertInput)} data-testid="cert-add"
                          className="h-9 px-3 rounded-lg bg-[#4F46E5] text-white text-[12px] font-semibold">Ajouter</button>
                </div>
              </div>
              <div className="rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] p-3">
                <div className="text-[12.5px] font-semibold text-[#4A4B63] mb-2">Langues parlées</div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(values.languages || []).map((l, i) => (
                    <span key={`${l}-${i}`} data-testid={`lang-chip-${i}`} className="px-2 h-7 rounded-full bg-[#ECFEFF] text-[#0E7490] text-[12px] font-semibold flex items-center gap-1.5">
                      {l}
                      <button type="button" onClick={() => removeChip('languages', i)} className="hover:text-[#B91C1C]">×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={langInput} onChange={(e) => setLangInput(e.target.value)} placeholder="Ex: Anglais (B2)"
                         onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addChip('languages', langInput, setLangInput); } }}
                         data-testid="lang-input"
                         className="flex-1 h-9 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px]" />
                  <button type="button" onClick={() => addChip('languages', langInput, setLangInput)} data-testid="lang-add"
                          className="h-9 px-3 rounded-lg bg-[#06B6D4] text-white text-[12px] font-semibold">Ajouter</button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Sessions animées (compteur)" type="number" value={values.sessions} onChange={(v) => set('sessions', v)} testId="trainer-field-sessions" />
                <Field label="Note moyenne" type="number" value={values.rating} onChange={(v) => set('rating', v)} testId="trainer-field-rating" />
              </div>
            </>
          )}

          {error && (
            <div className="text-[12px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">{error}</div>
          )}
          <DialogFooter className="pt-2">
            <button type="button" onClick={() => onOpenChange(false)}
                    className="h-10 px-4 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold text-[#4A4B63] hover:border-[#C7D2FE]">
              Annuler
            </button>
            <button type="submit" disabled={loading} data-testid="trainer-submit"
                    className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm hover:opacity-95 disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {initialValues ? 'Enregistrer' : 'Créer'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = 'text', as, options = [], required, full, testId, placeholder }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">
        {label}{required && <span className="text-[#EF4444] ml-0.5">*</span>}
      </label>
      {as === 'select' ? (
        <select
          value={value || ''} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testId}
          className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
        >
          <option value="">Sélectionner…</option>
          {options.map((o) => {
            const v = typeof o === 'string' ? o : o.value;
            const l = typeof o === 'string' ? o : (o.label ?? o.value);
            return <option key={v} value={v}>{l}</option>;
          })}
        </select>
      ) : (
        <input type={type} value={value ?? ''} placeholder={placeholder} required={required} data-testid={testId}
               onChange={(e) => onChange(type === 'number' ? Number(e.target.value) : e.target.value)}
               className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors" />
      )}
    </div>
  );
}

/** Dialog to send subcontracting contract (terms + send via Resend) */
function ContractDialog({ open, onOpenChange, trainer, onSent }) {
  const [form, setForm] = useState({
    mission_description: 'Prestation de formation en sous-traitance.',
    start_date: new Date().toISOString().slice(0, 10),
    end_date: '',
    daily_rate: '',
    notes: '',
  });
  const [sending, setSending] = useState(false);

  useEffect(() => { if (open) setSending(false); }, [open]);

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/trainers/${trainer.id}/contract/send`, {
        mission_description: form.mission_description,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        daily_rate: form.daily_rate ? Number(form.daily_rate) : null,
        notes: form.notes || null,
      });
      onSent?.(data);
      toast({
        title: data.email_sent ? 'Contrat envoyé' : 'Contrat généré',
        description: data.email_sent ? `Email envoyé à ${data.email_to}` : 'Resend non configuré — lien public disponible',
      });
      onOpenChange(false);
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Envoi impossible' });
    }
    setSending(false);
  };

  if (!trainer) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] rounded-2xl">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Contrat de sous-traitance</DialogTitle>
          <DialogDescription className="text-[13px]">
            Génère un PDF Qualiopi-conforme et envoie un lien de signature électronique à <strong>{trainer.email}</strong>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Objet / Mission</label>
            <textarea rows={3} value={form.mission_description} onChange={(e) => setForm((s) => ({ ...s, mission_description: e.target.value }))}
                      data-testid="contract-mission"
                      className="w-full px-3 py-2 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Date de début</label>
              <input type="date" value={form.start_date} data-testid="contract-start"
                     onChange={(e) => setForm((s) => ({ ...s, start_date: e.target.value }))}
                     className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px]" />
            </div>
            <div>
              <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Date de fin</label>
              <input type="date" value={form.end_date} data-testid="contract-end"
                     onChange={(e) => setForm((s) => ({ ...s, end_date: e.target.value }))}
                     className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px]" />
            </div>
          </div>
          <div>
            <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Taux journalier (€ HT)</label>
            <input type="number" step="10" value={form.daily_rate} data-testid="contract-rate"
                   onChange={(e) => setForm((s) => ({ ...s, daily_rate: e.target.value }))}
                   placeholder="Ex: 600"
                   className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px]" />
          </div>
          <div>
            <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Notes complémentaires</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
                      data-testid="contract-notes"
                      className="w-full px-3 py-2 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] resize-none" />
          </div>
        </div>
        <DialogFooter className="pt-3">
          <button type="button" onClick={() => onOpenChange(false)}
                  className="h-10 px-4 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold text-[#4A4B63]">Annuler</button>
          <button type="button" onClick={submit} disabled={sending} data-testid="contract-send"
                  className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm disabled:opacity-60">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Générer et envoyer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Documents manager (upload/list/download/delete) — rendered inside DetailSheet */
function DocumentsSection({ trainer, onChange }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [type, setType] = useState('CV');
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/trainers/${trainer.id}/documents`);
      setDocs(data);
    } catch { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { if (trainer?.id) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [trainer?.id]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast({ title: 'Fichier trop volumineux', description: 'Max 5 Mo' }); return; }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const b64 = (ev.target.result || '').toString();
        try {
          await api.post(`/trainers/${trainer.id}/documents`, {
            filename: file.name, type, mime: file.type || 'application/octet-stream', content_b64: b64,
          });
          toast({ title: 'Document ajouté', description: file.name });
          await load();
          onChange?.();
        } catch (e) {
          toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Upload refusé' });
        }
        setUploading(false);
        if (fileRef.current) fileRef.current.value = '';
      };
      reader.readAsDataURL(file);
    } catch {
      setUploading(false);
    }
  };

  const handleDownload = async (d) => {
    try {
      const res = await api.get(`/trainers/${trainer.id}/documents/${d.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: d.mime }));
      const a = document.createElement('a');
      a.href = url; a.download = d.filename; document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Erreur téléchargement' });
    }
  };

  const handleDelete = async (d) => {
    if (!window.confirm(`Supprimer le document « ${d.filename} » ?`)) return;
    try {
      await api.delete(`/trainers/${trainer.id}/documents/${d.id}`);
      toast({ title: 'Document supprimé' });
      await load();
    } catch {
      toast({ title: 'Erreur' });
    }
  };

  return (
    <div className="pt-4 border-t border-[#EFF0F6]">
      <div className="text-[11.5px] font-semibold uppercase tracking-wider text-[#8E8FA5] mb-2">Documents ({docs.length})</div>
      <div className="flex items-center gap-2 mb-3">
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="doc-type-select"
                className="h-9 px-2 rounded-lg bg-[#F6F7FB] border border-[#EFF0F6] text-[12.5px]">
          {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <input ref={fileRef} type="file" hidden data-testid="doc-file-input"
               onChange={(e) => handleUpload(e.target.files?.[0])} />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="doc-upload-btn"
                className="h-9 px-3 rounded-lg bg-[#EEF2FF] text-[#4F46E5] text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#EDE8FF] disabled:opacity-60">
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          Ajouter un document
        </button>
      </div>
      {loading && <div className="text-[12px] text-[#8E8FA5]">Chargement…</div>}
      {!loading && docs.length === 0 && (
        <div className="text-[12px] text-[#8E8FA5] italic">Aucun document — ajoutez CV, diplômes, RIB, Kbis…</div>
      )}
      <ul className="space-y-2" data-testid="doc-list">
        {docs.map((d) => (
          <li key={d.id} className="flex items-center gap-2 p-2 rounded-lg bg-[#FAFAFE] border border-[#EFF0F6]">
            <FileText className="w-4 h-4 text-[#4F46E5] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">{d.filename}</div>
              <div className="text-[10.5px] text-[#8E8FA5]">{d.type} · {(d.size / 1024).toFixed(0)} Ko</div>
            </div>
            <button onClick={() => handleDownload(d)} data-testid={`doc-download-${d.id}`} className="h-7 w-7 rounded hover:bg-white flex items-center justify-center">
              <Download className="w-3.5 h-3.5 text-[#6B6C85]" />
            </button>
            <button onClick={() => handleDelete(d)} data-testid={`doc-delete-${d.id}`} className="h-7 w-7 rounded hover:bg-[#FEF2F2] flex items-center justify-center">
              <Trash2 className="w-3.5 h-3.5 text-[#B91C1C]" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Formateurs() {
  const [search, setSearch] = useState('');
  const [trainers, setTrainers] = useState([]);
  const [create, setCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [detail, setDetail] = useState(null);
  const [contractFor, setContractFor] = useState(null);
  const [extranetFor, setExtranetFor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('cards');
  const [calendar, setCalendar] = useState([]);

  const reload = () => api.get('/trainers').then(({ data }) => setTrainers(sortNewestFirst(data))).catch(() => {});

  useEffect(() => { reload().finally(() => setLoading(false)); }, []);
  useEffect(() => {
    if (view === 'calendar') {
      api.get('/calendar/trainers').then(({ data }) => setCalendar(data)).catch(() => {});
    }
  }, [view]);

  const handleCreate = async (values) => {
    const payload = { ...values, avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(values.name)}&background=7C5CFC&color=fff` };
    const { data } = await api.post('/trainers', payload);
    setTrainers((p) => [data, ...p]);
    toast({ title: 'Formateur ajouté', description: data.name });
  };
  const handleUpdate = async (values) => {
    const { data } = await api.put(`/trainers/${editing.id}`, values);
    setTrainers((p) => p.map((t) => t.id === data.id ? data : t));
    setDetail((d) => (d && d.id === data.id ? data : d));
    toast({ title: 'Formateur modifié', description: data.name });
  };
  const handleDelete = async () => {
    await api.delete(`/trainers/${deleting.id}`);
    setTrainers((p) => p.filter((t) => t.id !== deleting.id));
    toast({ title: 'Formateur supprimé' });
  };

  const filtered = trainers.filter((t) =>
    `${t.name || ''} ${t.speciality || ''} ${t.domain || ''} ${t.siret || ''}`.toLowerCase().includes(search.toLowerCase())
  );
  const byTrainer = calendar.reduce((acc, s) => { (acc[s.trainer] = acc[s.trainer] || []).push(s); return acc; }, {});
  const totalConflicts = calendar.filter((s) => s.conflict).length;

  const downloadContract = async (t) => {
    try {
      const res = await api.get(`/trainers/${t.id}/contract.pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `contrat-${t.name.replace(/\s+/g, '-').toLowerCase()}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Aucun contrat actif', description: e?.response?.data?.detail || 'Envoyez d\'abord un contrat au formateur' });
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher nom, spécialité, SIRET…" data-testid="trainer-search"
                 className="w-full h-10 pl-10 pr-4 rounded-xl bg-white border border-[#EFF0F6] text-[13.5px] focus:border-[#C7D2FE] outline-none" />
        </div>
        <div className="flex bg-white rounded-xl border border-[#EFF0F6] p-1">
          {[['cards', Grid3x3, 'Grille'], ['calendar', CalendarDays, 'Planning']].map(([v, Ic, label]) => (
            <button key={v} data-testid={`formateurs-view-${v}`} onClick={() => setView(v)} className={`h-8 px-3 rounded-lg text-[12.5px] font-semibold flex items-center gap-1.5 transition-colors ${view === v ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#6B6C85]'}`}>
              <Ic className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
        <button onClick={() => setCreate(true)} data-testid="trainer-new-btn"
                className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm md:ml-auto hover:opacity-95">
          <Plus className="w-4 h-4" /> Nouveau formateur
        </button>
      </div>

      {view === 'calendar' && (
        <div className="space-y-4">
          {totalConflicts > 0 && (
            <div className="rounded-xl bg-[#FEF2F2] border border-[#FECACA] px-4 py-3 flex items-center gap-2 text-[13px] text-[#B91C1C] font-semibold">
              <AlertTriangle className="w-4 h-4" /> {totalConflicts} conflit{totalConflicts > 1 ? 's' : ''} détecté{totalConflicts > 1 ? 's' : ''}.
            </div>
          )}
          {Object.entries(byTrainer).map(([trainer, rows]) => (
            <div key={trainer} className="soft-card p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-[14.5px] font-semibold text-[#1A1B2E]">{trainer}</h3>
                  <p className="text-[12px] text-[#8E8FA5]">{rows.length} session(s)</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {rows.map((s) => (
                  <div key={s.id} className={`p-3 rounded-xl border ${s.conflict ? 'border-[#FECACA] bg-[#FEF2F2]' : 'border-[#F3F4F8] bg-[#FAFAFE]'}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[12.5px] font-semibold">{s.title}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[11.5px] text-[#4A4B63] mt-1">
                      <span>{new Date(s.startDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</span>
                      <span>·</span><span>{s.duration}h</span>
                      <span>·</span><span className="inline-flex items-center gap-1">{s.mode === 'Distanciel' ? <Video className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}{s.location}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {view === 'cards' && (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading && Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
        {!loading && filtered.length === 0 && (
          <div className="col-span-full">
            <EmptyState icon={Users} title="Aucun formateur" description="Ajoutez votre premier formateur."
                        action={<button onClick={() => setCreate(true)} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold">Nouveau formateur</button>} />
          </div>
        )}
        {!loading && filtered.map((t) => (
          <div key={t.id} className="soft-card p-5 cursor-pointer relative" onClick={() => setExtranetFor(t)} data-testid={`trainer-card-${t.id}`}>
            <div className="absolute top-3 right-3" onClick={(e) => e.stopPropagation()}>
              <RowActions
                onView={() => setExtranetFor(t)}
                onEdit={() => setEditing(t)}
                onDelete={() => setDeleting(t)}
              />
            </div>
            <div className="flex items-start gap-4">
              <img src={t.avatar} alt={t.name} className="w-14 h-14 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <h3 className="text-[15px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t.name}</h3>
                <p className="text-[12.5px] text-[#6B6C85] mt-0.5">{t.speciality}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  <span className={`text-[10.5px] font-semibold px-1.5 py-0.5 rounded ${t.status === 'Interne' ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'bg-[#E6FBF7] text-[#0F766E]'}`}>{t.status}</span>
                  {t.domain && <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-[#FFF7ED] text-[#C2410C]">{t.domain}</span>}
                  {/* Vague 111 — Extranet connection status at a glance */}
                  {t.lifecycle_status === 'active' && (
                    <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-[#E6FBF7] text-[#0F766E] inline-flex items-center gap-0.5" title="L'extranet est activé">
                      <ShieldCheck className="w-3 h-3" /> Extranet actif
                    </span>
                  )}
                  {t.lifecycle_status === 'invited' && (
                    <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E]" title="Invitation envoyée, en attente d'activation">Invité</span>
                  )}
                  {t.lifecycle_status === 'suspended' && (
                    <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-[#FEF2F2] text-[#B91C1C]">Extranet suspendu</span>
                  )}
                  {(!t.lifecycle_status || t.lifecycle_status === 'draft') && (
                    <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-[#F3F4F8] text-[#6B6C85]">Pas encore invité</span>
                  )}
                  {t.active_contract?.status === 'Signé' && (
                    <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-[#E6FBF7] text-[#0F766E] inline-flex items-center gap-0.5">
                      <BadgeCheck className="w-3 h-3" /> Contrat signé
                    </span>
                  )}
                  {t.active_contract?.status === 'Envoyé' && (
                    <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E]">Contrat en attente</span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 mt-1.5 text-[12px] text-[#8E8FA5]"><Mail className="w-3.5 h-3.5" /> {t.email}</div>
                {t.last_extranet_access_at && (
                  <div className="flex items-center gap-1.5 mt-1 text-[11.5px] text-[#6B6C85]" title={new Date(t.last_extranet_access_at).toLocaleString('fr-FR')} data-testid={`trainer-last-access-${t.id}`}>
                    <Clock className="w-3 h-3" /> {formatRelativeConnection(t.last_extranet_access_at)}
                  </div>
                )}
                {!t.last_extranet_access_at && t.lifecycle_status === 'active' && (
                  <div className="flex items-center gap-1.5 mt-1 text-[11.5px] text-[#8E8FA5] italic" data-testid={`trainer-last-access-${t.id}`}>
                    <Clock className="w-3 h-3" /> Jamais connecté
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-[#F3F4F8] grid grid-cols-3 gap-2">
              <div><div className="flex items-center gap-1 text-[11px] text-[#8E8FA5]"><CalendarDays className="w-3 h-3" /> Sessions</div><div className="text-[16px] font-bold">{t.sessions || 0}</div></div>
              <div><div className="flex items-center gap-1 text-[11px] text-[#8E8FA5]"><Star className="w-3 h-3" /> Note</div><div className="text-[16px] font-bold">{t.rating || 0}</div></div>
              <div><div className="flex items-center gap-1 text-[11px] text-[#8E8FA5]"><Award className="w-3 h-3" /> Docs</div><div className="text-[16px] font-bold text-[#0F766E]">{(t.docs || []).length}</div></div>
            </div>
            <div className="mt-4 flex gap-2" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setExtranetFor(t)} data-testid={`trainer-manage-${t.id}`}
                      className="flex-1 h-10 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center justify-center gap-2 hover:opacity-95">
                <ShieldCheck className="w-4 h-4" /> Gérer
              </button>
            </div>
          </div>
        ))}
      </div>
      )}

      <TrainerFormDialog open={create} onOpenChange={setCreate} onSubmit={handleCreate} />
      <TrainerFormDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} initialValues={editing} onSubmit={handleUpdate} />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} title="Supprimer ce formateur ?" description={deleting?.name} onConfirm={handleDelete} />
      <ContractDialog open={!!contractFor} onOpenChange={(o) => !o && setContractFor(null)} trainer={contractFor} onSent={reload} />
      <TrainerExtranetDialog
        open={!!extranetFor}
        onOpenChange={(o) => !o && setExtranetFor(null)}
        trainer={extranetFor}
        onChange={reload}
        onEdit={(t) => { setExtranetFor(null); setEditing(t); }}
        onDelete={(t) => { setExtranetFor(null); setDeleting(t); }}
        onSendContract={(t) => { setExtranetFor(null); setContractFor(t); }}
        onDownloadContract={(t) => downloadContract(t)}
        onDownloadIcs={async (t) => {
          try {
            const res = await api.get(`/trainers/${encodeURIComponent(t.name)}/calendar.ics`, { responseType: 'blob' });
            const blob = new Blob([res.data], { type: 'text/calendar' });
            const href = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = href; a.download = `${t.name.replace(/\s+/g, '-').toLowerCase()}.ics`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(href);
          } catch { toast({ title: 'Erreur téléchargement planning' }); }
        }}
      />
    </div>
  );
}
